/**
 * TextField drawing from the runtime's layout (`renderData.textInfo`): background, border, runs
 * (font/size/colour/bold/italic/underline, letter spacing), selection highlight and input caret.
 *
 * Positions come from the runtime layout (measured with platform.measureText, which uses the same
 * CSS font as drawing, see platform/browser-fonts.ts); the caret/selection x positions are
 * measured with the same runtime measurer so they line up with the layout in every host.
 * Colour transforms apply to all colours; text is clipped to the field bounds, and the lines
 * scrolled out above scrollV are not drawn at all (Flash starts a scrolled field at its top
 * gutter: the bottom of the previous line never shows in the 2 px gutter).
 * UNVERIFIED: device-font quirks of Flash Player (no alpha/rotation for device text) are not
 * emulated; selection colours (black highlight, white text) follow Ruffle.
 */
import type { LayoutLine, LayoutRun } from '../text/layout';
import { measureText } from '../text/metrics';
import type { FontResolver } from '../platform/browser-fonts';
import { CT, ColorStages, Mat, cssColor as cssColorBase, matScale } from './math';

export interface TextRenderInfo {
  bounds: { x: number; y: number; width: number; height: number };
  border: boolean;
  borderColor: number;
  background: boolean;
  backgroundColor: number;
  embedFonts: boolean;
  lines: LayoutLine[];
  offsetX: number;
  offsetY: number;
  /** Index of the first line shown (scrollV - 1): lines above it are scrolled out and not drawn. */
  firstLine?: number;
  focused: boolean;
  selection: [number, number];
  type: string;
}

function spec(run: LayoutRun) {
  const f = run.fmt;
  return { family: f.font, size: f.size, bold: f.bold, italic: f.italic };
}

/** x offset (field-local) of character index `idx` inside `run`. */
function xInRun(run: LayoutRun, idx: number): number {
  const k = Math.max(0, Math.min(run.text.length, idx - run.start));
  if (k === 0) return run.x;
  const sub = run.text.slice(0, k);
  return run.x + measureText(sub, spec(run), run.noCache) + run.fmt.letterSpacing * k;
}

function fillRun(ctx: CanvasRenderingContext2D, run: LayoutRun, text: string, x: number, baseline: number): void {
  const ls = run.fmt.letterSpacing;
  if (!ls) {
    ctx.fillText(text, x, baseline);
    return;
  }
  const sp = spec(run);
  let cx = x;
  for (const ch of text) {
    ctx.fillText(ch, cx, baseline);
    cx += measureText(ch, sp, run.noCache) + ls;
  }
}

/** Caret x/line for character index `idx` (end of text when past the last run). */
function caretPos(lines: LayoutLine[], idx: number): { line: LayoutLine; x: number } | null {
  if (!lines.length) return null;
  // The caret belongs to the last line starting at or before it (a wrapped line's end index is
  // the next line's start: Flash shows the caret at the start of the next line).
  let l = lines[0];
  for (const cand of lines) if (cand.start <= idx) l = cand;
  if (!l.runs.length) return { line: l, x: l.x };
  for (const r of l.runs) if (idx >= r.start && idx <= r.end) return { line: l, x: xInRun(r, idx) };
  const last = l.runs[l.runs.length - 1];
  return { line: l, x: idx < last.start ? l.runs[0].x : last.x + last.width };
}

export interface TextDrawOptions {
  fonts: FontResolver;
  /** Draw the caret of a focused input field (blink phase decided by the caller). */
  caretVisible: boolean;
  /** Caret colour on lines without text (the field's defaultTextFormat colour). */
  defaultColor?: number;
  /** Outer colour stages (distributed colour-matrix filters), applied after `ct`. */
  outer?: ColorStages | null;
}

export function drawText(ctx: CanvasRenderingContext2D, info: TextRenderInfo, m: Mat, ct: CT, opts: TextDrawOptions): void {
  const outer = opts.outer ?? null;
  const cssColor = (rgb: number, alpha: number, t: CT) => cssColorBase(rgb, alpha, t, outer);
  const b = info.bounds;
  if (!(b.width > 0 && b.height > 0)) return;
  const px = 1 / (matScale(m) || 1);
  ctx.setTransform(m[0], m[1], m[2], m[3], m[4], m[5]);
  ctx.globalAlpha = 1;
  if (info.background) {
    ctx.fillStyle = cssColor(info.backgroundColor, 1, ct);
    ctx.fillRect(b.x, b.y, b.width, b.height);
  }
  ctx.save();
  ctx.beginPath();
  ctx.rect(b.x, b.y, b.width, b.height);
  ctx.clip();
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  const [selA, selB] = info.selection;
  const showSel = info.focused && selB > selA;
  const top = b.y;
  const bottom = b.y + b.height;
  let lastFont = '';
  const firstLine = info.firstLine ?? 0;
  for (const line of info.lines) {
    if (line.index < firstLine) continue;
    const lineTop = info.offsetY + line.y;
    if (lineTop > bottom) break;
    if (lineTop + line.ascent + line.descent < top) continue;
    const baseline = lineTop + line.ascent;
    for (const run of line.runs) {
      if (!run.text) continue;
      const font = opts.fonts.cssFont(spec(run));
      if (font !== lastFont) {
        ctx.font = font;
        lastFont = font;
      }
      const x = info.offsetX + run.x;
      ctx.fillStyle = cssColor(run.fmt.color, 1, ct);
      fillRun(ctx, run, run.text, x, baseline);
      if (run.fmt.underline) {
        const uy = baseline + Math.max(px, run.fmt.size * 0.1);
        ctx.fillRect(x, uy, run.width, Math.max(px, run.fmt.size / 16));
      }
      if (showSel && selA < run.end && selB > run.start) {
        const a = Math.max(selA, run.start);
        const e = Math.min(selB, run.end);
        const xa = info.offsetX + xInRun(run, a);
        const xe = info.offsetX + xInRun(run, e);
        ctx.fillStyle = cssColor(0x000000, 1, ct);
        ctx.fillRect(xa, lineTop, xe - xa, line.ascent + line.descent);
        ctx.fillStyle = cssColor(0xffffff, 1, ct);
        fillRun(ctx, run, run.text.slice(a - run.start, e - run.start), xa, baseline);
      }
    }
  }
  if (info.focused && info.type === 'input' && opts.caretVisible && selA === selB) {
    const cp = caretPos(info.lines, selB);
    if (cp) {
      const lineTop = info.offsetY + cp.line.y;
      // Caret in the colour of the text at the caret (Flash/Ruffle), else the default format colour.
      let color = opts.defaultColor ?? 0;
      for (const r of cp.line.runs) if (selB >= r.start && selB <= r.end) color = r.fmt.color;
      if (cp.line.runs.length && !cp.line.runs.some((r) => selB >= r.start && selB <= r.end)) color = cp.line.runs[cp.line.runs.length - 1].fmt.color;
      ctx.fillStyle = cssColor(color, 1, ct);
      ctx.fillRect(info.offsetX + cp.x, lineTop, Math.max(px, 1), cp.line.ascent + cp.line.descent);
    }
  }
  ctx.restore();
  if (info.border) {
    ctx.setTransform(m[0], m[1], m[2], m[3], m[4], m[5]);
    ctx.strokeStyle = cssColor(info.borderColor, 1, ct);
    ctx.lineWidth = px;
    ctx.strokeRect(b.x + px / 2, b.y + px / 2, Math.max(0, b.width - px), Math.max(0, b.height - px));
  }
}
